import { Component, EventEmitter, HostListener, Input, OnChanges, OnInit, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, Validators, ReactiveFormsModule, AbstractControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as moment from 'moment';


@Component({
  selector: 'app-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, HttpClientModule]
})
export class DynamicFormComponent implements OnInit, OnChanges, OnDestroy {
  form!: FormGroup;
  schema: any[] = [];
  schemaError: string | null = null;
  formTitle: string | null = null;
  canReset: boolean = false;
  private initialValues: Record<string, any> = {};
  private formSub: any;
  submitValidationFields: string[] = [];
  customSubmitValidation: ((formValue: any) => boolean) | null = null;
  @Input() jsonSchema: any | null = null;
  @Input() defaultValues: Record<string, any> | null = null;
  @Input() submitLabel: string = 'Submit';   // default value
  @Input() resetLabel: string = 'Reset';
  @Input() resetFields: string[] | null = null; // field names to reset (schema keys)
  @Input() resetValues: Record<string, any> | null = null; // optional explicit values per field
  @Input() submitUrl: string | null = null; // optional endpoint to POST the form to
  @Input() showActions: boolean = true; // set false when the host renders its own submit/cancel buttons
  @Input() layout: 'stacked' | 'horizontal' = 'stacked';

  @Output() formChange = new EventEmitter<{ value: any; valid: boolean }>();
  @Output() schemaValidChange = new EventEmitter<boolean>();
  @Output() schemaErrorChange = new EventEmitter<string | null>();
  @Output() formSubmit = new EventEmitter<any>();

  // Outbox-style file upload state
  fileToUpload: File | null = null;
  fileName: string = '';
  showPreview: boolean = false;

  // Outbox-style dropdown logic
  dropdownStates: { [key: string]: boolean } = {};
  multiSearchQueries: Record<string, string> = {};

  constructor(private route: ActivatedRoute, private http: HttpClient) { }

  ngOnInit(): void {
    this.form = new FormGroup({});
    const effectiveSchema = this.jsonSchema || null;
    this.defaultValues = this.defaultValues || null;
    if (effectiveSchema) {
      this.applyJsonSchema(effectiveSchema);
    }
  }

  ngOnDestroy(): void {
    if (this.formSub && typeof this.formSub.unsubscribe === 'function') {
      this.formSub.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['jsonSchema'] && this.jsonSchema) {
      this.applyJsonSchema(this.jsonSchema);
    }
  }

  private applyJsonSchema(schema: any): void {
    // Validate schema first
    const validation = this.validateSchema(schema);
    if (!validation.valid) {
      this.schemaError = validation.error || 'Invalid schema';
      this.schemaErrorChange.emit(this.schemaError);
      this.schemaValidChange.emit(false);
      this.schema = [];
      this.form = new FormGroup({});
      this.formTitle = null;
      return;
    }
    this.schemaError = null;
    this.schemaErrorChange.emit(null);
    this.schemaValidChange.emit(true);
    this.formTitle = typeof schema.title === 'string' && schema.title.trim().length ? schema.title.trim() : null;
    // Extract submit validation rules
    this.submitValidationFields = schema.submitValidation?.fields || [];
    this.customSubmitValidation = schema.submitValidation?.custom || null;
    this.schema = this.convertJsonSchemaToItems(schema);
    this.form = new FormGroup({});
    this.multiSearchQueries = {};
    this.buildForm(this.schema, this.form);
    this.loadDropdownOptions(this.schema);
    // Apply default values [schema defaults first, then provided defaults]
    const schemaDefaults: Record<string, any> = {};
    this.schema.forEach(item => {
      const key = this.toKey(item.label);
      if (item.default !== undefined) schemaDefaults[key] = item.default;
    });
    const providedDefaults = this.defaultValues || {};
    this.form.patchValue({ ...schemaDefaults, ...providedDefaults }, { emitEvent: false });
    this.initialValues = this.form.getRawValue();
    if (this.formSub && typeof this.formSub.unsubscribe === 'function') this.formSub.unsubscribe();
    this.canReset = false;
    // Emit immediately with initial values
    this.formChange.emit({ value: this.form.getRawValue(), valid: this.form.valid });
    this.formSub = this.form.valueChanges.subscribe(() => {
      this.canReset = this.hasChanges();
      this.formChange.emit({ value: this.form.getRawValue(), valid: this.form.valid });
    });
  }

  private hasChanges(): boolean {
    const current = this.form.getRawValue();
    const keys = Object.keys(current);
    for (const key of keys) {
      const currVal = current[key];
      const initVal = this.initialValues[key];
      if (currVal instanceof File || initVal instanceof File) {
        if (!(currVal instanceof File) || !(initVal instanceof File)) return true;
        if (currVal.name !== initVal.name || currVal.size !== initVal.size || currVal.type !== initVal.type) return true;
        continue;
      }
      if (currVal && typeof currVal === 'object' && typeof (currVal as any).length === 'number' && currVal.item) {
        // FileList
        const fl = currVal as FileList;
        const il = initVal as FileList | null | undefined;
        const ilArr = il ? Array.from(il as FileList) : [];
        const flArr = Array.from(fl);
        if (flArr.length !== ilArr.length) return true;
        for (let i = 0; i < flArr.length; i++) {
          if (flArr[i].name !== ilArr[i]?.name || flArr[i].size !== ilArr[i]?.size || flArr[i].type !== ilArr[i]?.type) return true;
        }
        continue;
      }
      if (JSON.stringify(currVal) !== JSON.stringify(initVal)) return true;
    }
    return false;
  }

  private validateSchema(schema: any): { valid: boolean; error?: string } {
    if (schema === null || schema === undefined) return { valid: false, error: 'Schema is null or undefined' };
    if (typeof schema !== 'object') return { valid: false, error: 'Schema must be an object' };
    if (Object.keys(schema).length === 0) return { valid: false, error: 'Schema cannot be an empty object' };
    if (schema.type !== 'object') return { valid: false, error: 'Schema type must be "object"' };
    if (!schema.properties || typeof schema.properties !== 'object') {
      return { valid: false, error: 'Schema.properties must be an object' };
    }
    if (Object.keys(schema.properties).length === 0) {
      return { valid: false, error: 'Schema properties must not be empty' };
    }
    for (const key of Object.keys(schema.properties)) {
      const prop = schema.properties[key];
      if (!prop || typeof prop !== 'object') return { valid: false, error: `Property ${key} must be an object` };
      if (!prop.type) return { valid: false, error: `Property ${key} must have a type` };
      if (prop.input === 'dropdown' || Array.isArray(prop.enum) || Array.isArray(prop.options)) {
        const options = Array.isArray(prop.enum) ? prop.enum : prop.options;
        if (options && !Array.isArray(options)) return { valid: false, error: `Property ${key} options must be an array` };
      }
      if (prop.pattern) {
        try {
          // eslint-disable-next-line no-new
          new RegExp(prop.pattern);
        } catch {
          return { valid: false, error: `Property ${key} has invalid pattern regex` };
        }
      }
    }
    return { valid: true };
  }

  private convertJsonSchemaToItems(schema: any): any[] {
    const items: any[] = [];
    const properties = schema.properties || {};
    Object.keys(properties).forEach((propKey) => {
      const prop = properties[propKey];
      const isEnum = Array.isArray(prop.enum);
      const hasOptions = Array.isArray(prop.options);
      const widget = prop.widget || prop.input; // allow custom hint
      const isPassword = widget === 'password' || prop.format === 'password';
      const item: any = {
        label: prop.label || propKey,
        // type: prop.type === 'integer' ? 'number' : prop.type,
        type: prop.type === 'integer' ? 'integer' : (prop.type === 'number' ? 'number' : prop.type),
        input: ((): string => {
          if (widget === 'group-box') return 'group-box';
          if (widget === 'segmented' || widget === 'segmented-toggle') return 'segmented';
          if (prop.type === 'boolean') {
            if (widget === 'switch' || widget === 'toggle') return 'switch';
            return 'checkbox';
          }
          if (widget === 'file' || prop.input === 'file') return 'file';
          if (widget === 'multi-select' || (prop.type === 'array' && (isEnum || hasOptions))) return 'multi-select';
          if (isEnum || hasOptions) {
            if (widget === 'radio') return 'radio';
            if (widget === 'dropdown' || !widget) return 'dropdown';
          }
          if (isPassword) return 'password';
          if (widget === 'radio') return 'radio';
          if (widget === 'checkbox') return 'checkbox'; // (boolean preferred)
          if (widget === 'textarea') return 'textarea';
          if (widget === 'datetime-local') return 'datetime-local';
          if (widget === 'date') return 'date';
          if (widget === 'time') return 'time';
          if (widget === 'color') return 'color';
          return 'textbox';
        })(),
        required: Array.isArray(schema.required) ? schema.required.includes(propKey) : false,
        errorMessage: prop.description || `${propKey} is invalid`,
        minLength: prop.minLength,
        maxLength: prop.maxLength,
        minimum: prop.minimum,
        maximum: prop.maximum,
        default: prop.default,
        options: isEnum ? prop.enum : (hasOptions ? prop.options : undefined),
        optionsUrl: prop.optionsUrl,
        pattern: prop.pattern || prop.regex || undefined,
        optionLabel: prop.optionLabel,
        optionValue: prop.optionValue,
        format: isPassword ? 'password' : prop.format,
        passwordPolicy: prop.passwordPolicy,
        errorMessages: prop.errorMessages || undefined,
        customValidation: typeof prop.customValidation === 'function' ? prop.customValidation : undefined,
        placeholder: prop.placeholder,
        // file specific
        accept: prop.accept,
        multiple: !!prop.multiple,
        maxSize: typeof prop.maxSize === 'number' ? prop.maxSize : undefined,
        allowedExtensions: Array.isArray(prop.allowedExtensions) ? prop.allowedExtensions : undefined,
        allowedTypes: Array.isArray(prop.allowedTypes) ? prop.allowedTypes : undefined,
        // file helper content
        sampleUrl: prop.sampleUrl,
        helperText: prop.helperText,
        // group-box children (optional)
        group: Array.isArray(prop.group) ? prop.group : undefined
      };
      items.push(item);
    });
    return items;
  }

  private loadDropdownOptions(items: any[]): void {
    items.forEach((item) => {
      if (item.group && Array.isArray(item.group)) {
        this.loadDropdownOptions(item.group);
        return;
      }
      if (item.input === 'dropdown' && !item.options && item.optionsUrl) {
        this.http.get<any>(item.optionsUrl)
          .pipe(catchError(() => of([])))
          .subscribe((resp: any) => {
            const arr = Array.isArray(resp) ? resp : (Array.isArray(resp?.data) ? resp.data : []);
            item.options = arr;
          });
      }
    });
  }

  private getGroupForm(groupLabel: string): FormGroup | null {
    const key = this.toKey(groupLabel);
    const ctrl = this.form.get(key);
    return (ctrl instanceof FormGroup) ? ctrl : null;
  }

  onGroupSubmit(item: any): void {
    const grp = this.getGroupForm(item.label);
    if (!grp) return;
    if (!grp.valid) {
      grp.markAllAsTouched();
      return;
    }
    const value = grp.getRawValue();
    console.log('Group Submit:', item.label, value);
    Helpers.smallAlertBox("Success", `${item.label}: submitted`, "Success", 3500);
  }

  onGroupReset(item: any): void {
    const grp = this.getGroupForm(item.label);
    if (!grp) return;
    // Reset to default values of children if defined, else blank/null
    (item.group || []).forEach((child: any) => {
      const key = this.toKey(child.label);
      const ctrl = grp.get(key);
      if (!ctrl) return;
      const defVal = (child && child.default !== undefined)
        ? child.default
        : (child.input === 'checkbox' || child.type === 'boolean') ? false
        : (child.type === 'number') ? null
        : '';
      ctrl.reset(defVal);
    });
  }

  private buildForm(items: any[], group: FormGroup): void {
    items.forEach((item) => {
      if (item.group && Array.isArray(item.group)) {
        const hasLabel = typeof item.label === 'string' && item.label.trim().length > 0;
        if (hasLabel) {
          const nested = new FormGroup({});
          group.addControl(this.toKey(item.label), nested);
          this.buildForm(item.group, nested);
        } else {
          // No level: inject children into current group without adding a level key
          this.buildForm(item.group, group);
        }
      } else {
        const key = this.toKey(item.label);
        const validators = this.getValidators(item);
        const control = new FormControl(this.getDefaultValue(item), validators);
        group.addControl(key, control);
      }
    });
  }

  private getValidators(item: any) {
    const v = [] as any[];

    // Required
    if (item.required) {
      if (item.type === 'boolean' || item.input === 'checkbox' || item.input === 'switch') {
        // A required boolean has to be opted into, otherwise `false` would satisfy Validators.required
        v.push(Validators.requiredTrue);
      } else {
        v.push(Validators.required);
      }
    }

    // Length constraints
    if (typeof item.minLength === 'number') v.push(Validators.minLength(item.minLength));
    if (typeof item.maxLength === 'number') v.push(Validators.maxLength(item.maxLength));

    // Number constraints
    if (item.type === 'number') {
      if (typeof item.minimum === 'number') v.push(Validators.min(item.minimum));
      if (typeof item.maximum === 'number') v.push(Validators.max(item.maximum));
    }

    // Integer constraint
    if (item.type === 'integer') {
      if (typeof item.minimum === 'number') v.push(Validators.min(item.minimum));
      if (typeof item.maximum === 'number') v.push(Validators.max(item.maximum));

      v.push((control: AbstractControl) => {
        const value = control.value;
        if (value === null || value === '' || value === undefined) return null;
        return Number.isInteger(Number(value)) ? null : { integer: true };
      });
    }

    // Custom validation (e.g. duplicate name checks)
    if (typeof item.customValidation === 'function') {
      v.push((control: AbstractControl) => {
        const result = item.customValidation(control.value);
        if (result === true || result == null || result?.valid !== false) {
          return null;
        }
        return { custom: { message: result.message } };
      });
    }

    // Pattern
    if ((item.type === 'string' || item.input === 'textbox') && item.pattern) {
      try {
        const re = item.pattern instanceof RegExp ? item.pattern : new RegExp(item.pattern);
        v.push(Validators.pattern(re));
      } catch {
        // ignore invalid regex
        console.log("Invalid regex");

      }
    }
    // Password validation via format or policy
    if ((item.type === 'string' || item.input === 'textbox') && (item.format === 'password' || item.passwordPolicy)) {
      const policy = {
        minLength: item.minLength ?? 8,
        minUppercase: item.passwordPolicy?.minUppercase ?? 1,
        minLowercase: item.passwordPolicy?.minLowercase ?? 1,
        minDigits: item.passwordPolicy?.minDigits ?? 1,
        minSpecial: item.passwordPolicy?.minSpecial ?? 0,
        disallowWhitespace: item.passwordPolicy?.disallowWhitespace ?? true
      };
      v.push((control: AbstractControl) => {
        const value = control.value as string | null | undefined;
        if (!value) return null; // required handled separately
        const errors: any = {};
        if (typeof policy.minLength === 'number' && value.length < policy.minLength) {
          errors.passwordMinLength = { requiredLength: policy.minLength };
        }
        if (policy.minUppercase && (value.replace(/[^A-Z]/g, '').length < policy.minUppercase)) {
          errors.passwordUppercase = { required: policy.minUppercase };
        }
        if (policy.minLowercase && (value.replace(/[^a-z]/g, '').length < policy.minLowercase)) {
          errors.passwordLowercase = { required: policy.minLowercase };
        }
        if (policy.minDigits && (value.replace(/\D/g, '').length < policy.minDigits)) {
          errors.passwordDigit = { required: policy.minDigits };
        }
        if (policy.minSpecial && (value.replace(/[A-Za-z0-9]/g, '').length < policy.minSpecial)) {
          errors.passwordSpecial = { required: policy.minSpecial };
        }
        if (policy.disallowWhitespace && /\s/.test(value)) {
          errors.passwordNoWhitespace = true;
        }
        return Object.keys(errors).length ? errors : null;
      });
    }
    // File validators
    if (item.input === 'file') {
      v.push((control: AbstractControl) => {
        const value = control.value as File | FileList | null | undefined;
        if (!value) return null; // required handled separately
        const files: File[] = (value instanceof FileList)
          ? Array.from(value)
          : (value instanceof File ? [value] : []);
        const errors: any = {};
        // size
        if (typeof item.maxSize === 'number') {
          const tooBig = files.find(f => f.size > item.maxSize);
          if (tooBig) errors.fileMaxSize = { file: tooBig.name, maxSize: item.maxSize };
        }
        // extensions
        if (Array.isArray(item.allowedExtensions) && item.allowedExtensions.length) {
          const badExt = files.find(f => {
            const ext = (f.name.split('.').pop() || '').toLowerCase();
            return !item.allowedExtensions.map((e: string) => e.replace(/^\./, '').toLowerCase()).includes(ext);
          });
          if (badExt) errors.fileExtension = { file: badExt.name, allowed: item.allowedExtensions };
        }
        // mime types
        if (Array.isArray(item.allowedTypes) && item.allowedTypes.length) {
          const badType = files.find(f => !item.allowedTypes.includes(f.type));
          if (badType) errors.fileType = { file: badType.name, allowed: item.allowedTypes };
        }
        return Object.keys(errors).length ? errors : null;
      });
    }
    //date time validation
    if (item.input === 'datetime-local') {
      v.push((control: AbstractControl) => {
        const value: string = control.value;
        if (!value) return null;
    
        const errors: any = {};
    
        const mValue = moment(value, 'YYYY-MM-DDTHH:mm');
    
        if (item.minDate && mValue.isBefore(moment(item.minDate, 'YYYY-MM-DDTHH:mm'))) {
          errors.minDate = { requiredMin: item.minDate };
        }
    
        if (item.maxDate && mValue.isAfter(moment(item.maxDate, 'YYYY-MM-DDTHH:mm'))) {
          errors.maxDate = { requiredMax: item.maxDate };
        }
    
        return Object.keys(errors).length ? errors : null;
      });
    } 
    //date validation
    if (item.input === 'date') {
      v.push((control: AbstractControl) => {
        const value: string = control.value;
        if (!value) return null;
    
        const errors: any = {};
    
        const mValue = moment(value, 'YYYY-MM-DD');
    
        if (item.minDate && mValue.isBefore(moment(item.minDate, 'YYYY-MM-DD'))) {
          errors.minDate = { requiredMin: item.minDate };
        }
    
        if (item.maxDate && mValue.isAfter(moment(item.maxDate, 'YYYY-MM-DD'))) {
          errors.maxDate = { requiredMax: item.maxDate };
        }
    
        return Object.keys(errors).length ? errors : null;
      });
    } 
    //time validation
    if (item.input === 'time') {
      v.push((control: AbstractControl) => {
        const value: string = control.value;
        if (!value) return null;
    
        const errors: any = {};
    
        const mValue = moment(value, 'HH:mm');
    
        if (item.minDate && mValue.isBefore(moment(item.minDate, 'HH:mm'))) {
          errors.minDate = { requiredMin: item.minDate };
        }
    
        if (item.maxDate && mValue.isAfter(moment(item.maxDate, 'HH:mm'))) {
          errors.maxDate = { requiredMax: item.maxDate };
        }
    
        return Object.keys(errors).length ? errors : null;
      });
    }       
    // Color input validation (hex color like #RRGGBB or #RGB)
    if (item.input === 'color') {
      v.push(Validators.pattern(/^#(?:[0-9a-fA-F]{3}){1,2}$/));
    }
    return v;
  }

  // Segmented toggle helpers
  getSegmentedStyle(item: any) {
    const color = item.activeColor || '#3b82f6'; // default blue when active
    const text = item.activeTextColor || '#ffffff';
    return { '--seg-active-bg': color, '--seg-active-color': text } as any;
  }

  getSegmentLabel(item: any, index: number): string {
    const opts = this.getSegmentOptions(item);
    return String(opts[index]?.label ?? (index === 0 ? 'Basic' : 'Advanced'));
  }

  getSegmentValue(item: any, index: number): any {
    const opts = this.getSegmentOptions(item);
    return opts[index]?.value ?? (index === 0 ? false : true);
  }

  onSegmentedClick(item: any, index: number): void {
    const key = this.toKey(item.label);
    const ctrl = this.form.get(key);
    if (!ctrl) return;
    ctrl.setValue(this.getSegmentValue(item, index));
    ctrl.markAsTouched();
  }

  private getSegmentOptions(item: any): Array<{ label: string; value: any }> {
    // priority: item.options (array of strings or {label,value}), else enum, else boolean defaults
    let options = item.options || item.enum;
    if (Array.isArray(options) && options.length >= 2) {
      const normalized = options.slice(0, 2).map((o: any) => {
        if (o && typeof o === 'object') {
          const labelKey = item.optionLabel || 'label';
          const valueKey = item.optionValue || 'value';
          return { label: String(o[labelKey] ?? o[valueKey]), value: o[valueKey] };
        }
        return { label: String(o), value: o };
      });
      return normalized as Array<{ label: string; value: any }>;
    }
    return [
      { label: 'Basic', value: false },
      { label: 'Advanced', value: true }
    ];
  }

  private getDefaultValue(item: any) {
    if (item.default !== undefined) return item.default;
    if (item.input === 'file') return item.multiple ? null : null;
    if (item.input === 'multi-select' || item.type === 'array') return [];
    if (item.input === 'checkbox' || item.type === 'boolean') return false;
    if (item.type === 'number') return null;
    return '';
  }

  private getItemByKey(key: string): any | undefined {
    return this.schema.find(i => this.toKey(i.label) === key);
  }

  toKey(label: string): string {
    return (label || 'field')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
  }

  get isHorizontal(): boolean {
    return this.layout === 'horizontal';
  }

  placeholderOf(item: any, fallbackPrefix = 'Select'): string {
    if (item?.placeholder) {
      return item.placeholder;
    }
    return item?.input === 'dropdown' || item?.input === 'multi-select'
      ? `${fallbackPrefix} ${item.label}`
      : (item?.label || '');
  }

  hasFieldError(item: any): boolean {
    const control = this.form?.get(this.toKey(item.label));
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  getError(item: any, control: AbstractControl | null): string | null {
    if (!control || control.valid) return null;
    if (!control.touched && !control.dirty) return null;
    const em = item.errorMessages || {};
    if (control.hasError('required') || control.hasError('requiredTrue')) {
      return em.required || em.requiredTrue || item.errorMessage || `${item.label} is required`;
    }
    if (control.hasError('custom')) {
      return control.getError('custom')?.message || em.custom || item.errorMessage || 'Invalid value';
    }
    if (control.hasError('minlength')) {
      return em.minLength || em.minlength || `Minimum length is ${item.minLength}`;
    }
    if (control.hasError('maxlength')) {
      return em.maxLength || em.maxlength || `Maximum length is ${item.maxLength}`;
    }
    if (control.hasError('min')) {
      return em.minimum || em.min || `Minimum is ${item.minimum}`;
    }
    if (control.hasError('max')) {
      return em.maximum || em.max || `Maximum is ${item.maximum}`;
    }
    if (control.hasError('pattern')) {
      return em.pattern || 'Invalid format';
    }
    if (control.hasError('passwordMinLength')) {
      const r = control.getError('passwordMinLength')?.requiredLength;
      return em.passwordMinLength || `Password must be at least ${r} characters.`;
    }
    if (control.hasError('passwordUppercase')) {
      const r = control.getError('passwordUppercase')?.required;
      return em.passwordUppercase || `Password needs at least ${r} uppercase letter(s).`;
    }
    if (control.hasError('passwordLowercase')) {
      const r = control.getError('passwordLowercase')?.required;
      return em.passwordLowercase || `Password needs at least ${r} lowercase letter(s).`;
    }
    if (control.hasError('passwordDigit')) {
      const r = control.getError('passwordDigit')?.required;
      return em.passwordDigit || `Password needs at least ${r} number(s).`;
    }
    if (control.hasError('passwordSpecial')) {
      const r = control.getError('passwordSpecial')?.required;
      return em.passwordSpecial || `Password needs at least ${r} special character(s).`;
    }
    if (control.hasError('passwordNoWhitespace')) {
      return em.passwordNoWhitespace || 'Password cannot contain whitespace.';
    }
    if (control.hasError('integer')) {
      return em.integer || `${item.label} must be an integer (no decimals and string allowed)`;
    }
    if (control.hasError('fileMaxSize')) {
      const d = control.getError('fileMaxSize');
      return em.fileMaxSize || `File ${d?.file} exceeds max size of ${d?.maxSize} bytes`;
    }
    if (control.hasError('fileExtension')) {
      const d = control.getError('fileExtension');
      return em.fileExtension || `File ${d?.file} has invalid extension. Allowed: ${Array.isArray(d?.allowed) ? d.allowed.join(', ') : d?.allowed}`;
    }
    if (control.hasError('fileType')) {
      const d = control.getError('fileType');
      return em.fileType || `File ${d?.file} has invalid type. Allowed: ${Array.isArray(d?.allowed) ? d.allowed.join(', ') : d?.allowed}`;
    }
    if (control.hasError('minDate')) {
      const r = control.getError('minDate')?.requiredMin;
      return em.minDate || `Date must be on or after ${new Date(r).toLocaleString()}`;
    }
    if (control.hasError('maxDate')) {
      const r = control.getError('maxDate')?.requiredMax;
      return em.maxDate || `Date must be on or before ${new Date(r).toLocaleString()}`;
    }    
    return item.errorMessage || 'Invalid value';
  }

  onFileChange(item: any, event: Event): void {
    const input = event.target as HTMLInputElement;
    const key = this.toKey(item.label);
    const control = this.form.get(key);
    if (!control) return;
    const files = input.files;
    if (!files || files.length === 0) {
      control.setValue(null);
      control.markAsTouched();
      control.updateValueAndValidity();
      this.fileName = '';
      this.showPreview = false;
      return;
    }
    if (item.multiple) {
      // Validate all files
      const validFiles = Array.from(files).filter(file => this.isValidFileExtension(file, item));
      if (validFiles.length === 0) {
        Helpers.smallAlertBox("Error", 'File extension not supported.', "error", 3500)
        input.value = '';
        control.setValue(null);
        return;
      }
  
      control.setValue(validFiles);
      this.fileName = `${validFiles.length} file(s) selected`;
    } else {
      const file = files.item(0);
      if (file && this.isValidFileExtension(file, item)) {
        control.setValue(file);
        this.fileName = file.name;
        this.fileToUpload = file;
      } else {
        Helpers.smallAlertBox("Error", 'File extension not supported.', "error", 3500)
        input.value = '';
        control.setValue(null);
        return;
      }
    }
  
    control.markAsTouched();
    control.updateValueAndValidity();
    this.showPreview = true;
  }

  // Proxy to keep same method name used in HTML similar to outbox
  onFileSelectProxy(item: any, event: Event): void { this.onFileChange(item, event); }

  isValidFileExtension(file: File, item?: any): boolean {
    // If schema item provides allowedExtensions, use those; else fallback
    let validExtensions = ['csv', 'xlsx', 'xls'];
    if (item && Array.isArray(item.allowedExtensions) && item.allowedExtensions.length) {
      validExtensions = item.allowedExtensions.map((ext: string) => ext.replace(/^\./, '').toLowerCase());
    }
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    return !!fileExtension && validExtensions.includes(fileExtension);
  }

  onFileAdd(item: any): void {
    const key = this.toKey(item.label);
    const ctrl = this.form.get(key);
  
    if (!ctrl || !ctrl.value) {
      Helpers.smallAlertBox("Error", 'No file selected.', "error", 3500)
      return;
    }
  
    // Just show preview for now
    this.showPreview = true;
  }

  deleteFile(item: any): void {
    const key = this.toKey(item.label);
    const ctrl = this.form.get(key);
  
    if (ctrl) {
      ctrl.setValue(null);
      ctrl.markAsPristine();
      ctrl.updateValueAndValidity();
    }
  
    // also clear preview flag if you’re using it in UI
    this.showPreview = false;
  
    // reset the actual file input element
    const input = document.getElementById(key + '_file_upload') as HTMLInputElement;
    if (input) {
      input.value = '';
    }
  }
  
  private clearAllFiles(): void {
    this.schema.forEach((item: any) => {
      if (item.input === 'file') {
        this.deleteFile(item);
      }
    });
  }
  

  resetFileUploadState(): void {
    this.fileToUpload = null;
    this.fileName = '';
    this.showPreview = false;
  }

  isFormReady(): boolean {
    if (!this.form) return false;
    // Use custom validation if provided
    if (this.customSubmitValidation) {
      return this.customSubmitValidation(this.form.getRawValue());
    }
    // Use field-based validation if specified
    if (this.submitValidationFields.length > 0) {
      return this.submitValidationFields.every(fieldKey => {
        const value = this.form.get(fieldKey)?.value;
        return value !== null && value !== undefined && value !== '';
      });
    }
    // Default: form must be valid
    return this.form.valid;
  }

  private buildSubmissionPayload(): { payload: any; isFormData: boolean } {
    const raw = this.form.getRawValue();
    const keys = Object.keys(raw);
    const fileKeys: string[] = [];
    keys.forEach(k => {
      const item = this.getItemByKey(k);
      if (item && item.input === 'file') fileKeys.push(k);
    });
    if (fileKeys.length === 0) {
      return { payload: raw, isFormData: false };
    }
    const fd = new FormData();
    keys.forEach(k => {
      const value = raw[k];
      const item = this.getItemByKey(k);
      if (item && item.input === 'file') {
        if (value instanceof FileList) {
          Array.from(value).forEach((f, idx) => fd.append(k, f, f.name || `file_${idx}`));
        } else if (value instanceof File) {
          fd.append(k, value, value.name);
        }
      } else if (value !== undefined && value !== null) {
        fd.append(k, typeof value === 'object' ? JSON.stringify(value) : String(value));
      }
    });
    return { payload: fd, isFormData: true };
  }


  onSubmit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      console.warn('Form is invalid, cannot submit');
      return;
    }

    const { payload, isFormData } = this.buildSubmissionPayload();

    // Emit value to parent regardless of remote submission
    this.formSubmit.emit(payload instanceof FormData ? this.form.getRawValue() : payload);

    if (this.submitUrl && typeof this.submitUrl === 'string' && this.submitUrl.trim().length) {
      this.http.post<any>(this.submitUrl, payload).pipe(catchError((err) => {
        console.error('Submit failed', err);
        Helpers.smallAlertBox("Error", 'Submission failed. Plese try again.', "error", 3500)
        return of(null);
      })).subscribe((resp) => {
        if (resp !== null) {
          console.log('Submission success:', resp);
          Helpers.smallAlertBox("Success", 'Form submitted successfully!', "Success", 3500)
          this.onReset();
        }
      });
      return;
    }

    // Parent handles success/error when it listens to formSubmit
    if (this.formSubmit.observed) {
      return;
    }

    // Local-only submit success path
    console.log('Dynamic Form Submit (local):', this.form.getRawValue());
    Helpers.smallAlertBox("Success", 'Form submitted successfully!', "Success", 3500)
    this.onReset();
  }
  

  onReset(): void {
    if (!this.form) return;
    const controls = this.form.controls as Record<string, any>;
    const keysToReset = Array.isArray(this.resetFields) && this.resetFields.length
      ? this.resetFields.map(name => this.toKey(name))
      : Object.keys(controls);

    keysToReset.forEach(key => {
      const ctrl = this.form.get(key);
      if (!ctrl) return;
      let value: any = undefined;
      if (this.resetValues && Object.prototype.hasOwnProperty.call(this.resetValues, key)) {
        value = this.resetValues[key];
      } else {
        const item = this.getItemByKey(key);
        value = item ? this.getDefaultValue(item) : null;
      }
      ctrl.reset(value, { emitEvent: true });
      // Reset file input DOM value if file field
      const item = this.getItemByKey(key);
      if (item && item.input === 'file') {
        const inputEl = document.querySelector(`input[type="file"][name="${key}"]`) as HTMLInputElement | null;
        if (inputEl) inputEl.value = '';
      }
    });
    this.initialValues = this.form.getRawValue();
    this.canReset = false;
    this.clearAllFiles();
  }

  getFileName(item: any): string {
    const key = this.toKey(item.label);
    const v = this.form?.get(key)?.value;
    if (!v) return 'No file chosen';
    if (v instanceof FileList) return Array.from(v).map(f => f.name).join(', ');
    if (v instanceof File) return v.name;
    return 'No file chosen';
  }

  // Outbox-style dropdown logic
  toggleDropdown(item: any): void {
    const key = this.toKey(item.label);
    const next = !this.dropdownStates[key];
    this.closeAllCustomDropdowns();
    this.dropdownStates[key] = next;
    item.dropdownOpen = next;
  }

  openDropdown(item: any): void {
    const key = this.toKey(item.label);
    this.dropdownStates[key] = true;
    item.dropdownOpen = true;
  }

  closeDropdown(item: any): void {
    setTimeout(() => {
      const key = this.toKey(item.label);
      this.dropdownStates[key] = false;
      item.dropdownOpen = false;
    }, 200); // allow click event to register
  }

  clearDropdown(item: any, event: Event): void {
    event.stopPropagation();
    const key = this.toKey(item.label);
    this.form.get(key)?.setValue('');
    this.dropdownStates[key] = false;
    item.dropdownOpen = false;
  }

  selectDropdownOption(item: any, option: any): void {
    const key = this.toKey(item.label);
    this.form.get(key)?.setValue(option.value || option.label || option);
    this.dropdownStates[key] = false;
    item.dropdownOpen = false;
  }

  optionValueOf(item: any, option: any): any {
    if (option === null || option === undefined) {
      return option;
    }
    if (item?.optionValue && typeof option === 'object') {
      return option[item.optionValue];
    }
    return typeof option === 'object' ? (option.value ?? option.label ?? option) : option;
  }

  optionLabelOf(item: any, option: any): string {
    if (option === null || option === undefined) {
      return '';
    }
    if (item?.optionLabel && typeof option === 'object') {
      return option[item.optionLabel];
    }
    return typeof option === 'object' ? (option.label ?? option.value ?? '') : String(option);
  }

  getSelectedValues(item: any): any[] {
    const value = this.form?.get(this.toKey(item.label))?.value;
    return Array.isArray(value) ? value : [];
  }

  isMultiSelected(item: any, option: any): boolean {
    return this.getSelectedValues(item).includes(this.optionValueOf(item, option));
  }

  getOptionLabelForValue(item: any, value: any): string {
    const match = (item.options || []).find((option: any) => this.optionValueOf(item, option) === value);
    return match ? this.optionLabelOf(item, match) : String(value ?? '');
  }

  filteredMultiOptions(item: any): any[] {
    const query = (this.multiSearchQueries[this.toKey(item.label)] || '').trim().toLowerCase();
    const options = item.options || [];
    if (!query) {
      return options;
    }
    return options.filter((option: any) => this.optionLabelOf(item, option).toLowerCase().startsWith(query));
  }

  onMultiSearch(item: any, event: Event): void {
    const key = this.toKey(item.label);
    this.multiSearchQueries[key] = (event.target as HTMLInputElement).value || '';
  }

  toggleMultiValue(item: any, option: any, event?: Event): void {
    event?.stopPropagation();
    const key = this.toKey(item.label);
    const selected = this.optionValueOf(item, option);
    const current = [...this.getSelectedValues(item)];
    const index = current.indexOf(selected);
    if (index >= 0) {
      current.splice(index, 1);
    } else {
      current.push(selected);
    }
    this.form.get(key)?.setValue(current);
    this.form.get(key)?.markAsDirty();
    this.form.get(key)?.markAsTouched();
  }

  removeMultiValue(item: any, value: any, event: Event): void {
    event.stopPropagation();
    const key = this.toKey(item.label);
    const current = this.getSelectedValues(item).filter((entry) => entry !== value);
    this.form.get(key)?.setValue(current);
    this.form.get(key)?.markAsDirty();
    this.form.get(key)?.markAsTouched();
  }

  getSelectedSingleValue(item: any): any {
    const value = this.form?.get(this.toKey(item.label))?.value;
    return value === undefined || value === null || value === '' ? '' : value;
  }

  isSingleSelected(item: any, option: any): boolean {
    return this.getSelectedSingleValue(item) === this.optionValueOf(item, option);
  }

  selectSingleOption(item: any, option: any, event?: Event): void {
    event?.stopPropagation();
    const key = this.toKey(item.label);
    this.form.get(key)?.setValue(this.optionValueOf(item, option));
    this.form.get(key)?.markAsDirty();
    this.form.get(key)?.markAsTouched();
    this.dropdownStates[key] = false;
    item.dropdownOpen = false;
    this.multiSearchQueries[key] = '';
  }

  private closeAllCustomDropdowns(): void {
    (this.schema || []).forEach((schemaItem) => {
      if (schemaItem.input === 'multi-select' || schemaItem.input === 'dropdown') {
        const schemaKey = this.toKey(schemaItem.label);
        this.dropdownStates[schemaKey] = false;
        schemaItem.dropdownOpen = false;
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('.df-multiselect-dropdown')) {
      return;
    }
    this.closeAllCustomDropdowns();
  }
}


